import React, { useEffect, useMemo, useState } from 'react';
import SEOHead from '../components/SEOHead';
import { getPlaceSchema, getFAQSchema } from '../utils/seoSchemas';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';
import { stadiumsAPI, citiesAPI } from '../services/api';
import Map from '../components/Map';
import AdBanner from '../components/AdBanner';
import TravelLongformSections from '../components/TravelLongformSections';
import { getStadiumLongform } from '../content/travelLongform';
import { guideSlugForStadiumCity } from '../content/stadiumCityToGuideSlug';
import { itineraryHubPath } from '../content/guides/itineraryHubMeta';

function StadiumHero({ src, alt }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) return null;
  return (
    <div className="relative mb-6 h-48 sm:h-56 md:h-72 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-800">
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" aria-hidden />
    </div>
  );
}

const StadiumDetail = () => {
  const { slug } = useParams();
  const { data: stadium, isLoading } = useQuery({
    queryKey: ['stadium', slug],
    queryFn: () => stadiumsAPI.getById(slug).then((r) => r.data),
    enabled: !!slug,
  });
  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: () => citiesAPI.getAll().then((r) => r.data),
    enabled: !!stadium,
  });

  const cityGuide = useMemo(() => {
    if (!stadium?.city) return null;
    return cities.find((c) => c.name === stadium.city || stadium.city?.includes(c.name) || c.name?.includes(stadium.city));
  }, [cities, stadium]);

  const longform = useMemo(() => (stadium ? getStadiumLongform(stadium) : null), [stadium]);

  const itineraryHubForVenue = useMemo(() => {
    if (!stadium?.city) return null;
    const guideSlug = guideSlugForStadiumCity(stadium.city);
    return guideSlug ? itineraryHubPath(guideSlug) : null;
  }, [stadium]);

  const stadiumFaq = useMemo(() => {
    if (!stadium) return [];
    const cap = stadium.capacity ? `This venue lists capacity of about ${Number(stadium.capacity).toLocaleString()} spectators.` : '';
    const base = [
      {
        question: `Where is ${stadium.name} and which city hosts it?`,
        answer: `${stadium.name} is in ${stadium.city}, ${stadium.country}. Use the map below for orientation and the linked city guide for where to stay and how to get around.`,
      },
      {
        question: 'How early should I arrive on match day?',
        answer: `Plan to arrive several hours before kickoff for security screening and crowds. ${cap} Check official stadium and FIFA communications for gate times and prohibited items.`,
      },
      {
        question: 'How do I combine this stadium with other World Cup stops?',
        answer: 'Browse the match schedule for fixtures at this venue, then use the itinerary planner and travel planner to chain cities and flights.',
      },
    ];
    const extra = longform?.extraFaq?.length ? longform.extraFaq : [];
    return [...base, ...extra];
  }, [stadium, longform]);

  if (isLoading || !stadium) return <div className="p-8 text-center">Loading...</div>;

  const desc =
    longform?.metaDescription ||
    stadium.description ||
    `${stadium.name} in ${stadium.city}, ${stadium.country}. Capacity ${stadium.capacity?.toLocaleString()}. 2026 FIFA World Cup venue guide.`;

  const pageTitle = longform
    ? `${longform.seoTitleSuffix} | WorldCup Fan Planner`
    : `${stadium.name} – 2026 World Cup Stadium Guide | WorldCup Fan Planner`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={desc}
        schema={[getPlaceSchema(stadium), getFAQSchema(stadiumFaq)]}
        ogType="article"
      />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <StadiumHero src={stadium.image} alt={`${stadium.name}, ${stadium.city}`} />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {longform ? `${stadium.name} — FIFA World Cup 2026 fan guide` : stadium.name}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          {stadium.city}, {stadium.country}
        </p>
        <p className="text-amber-600 dark:text-amber-400 font-medium mt-2">Capacity: {stadium.capacity?.toLocaleString()}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Venue details are for fan planning—confirm access and transport with official stadium and FIFA announcements.
        </p>

        {stadium.description && <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">{stadium.description}</p>}

        {longform && <TravelLongformSections data={longform} />}

        <p className="mt-4 text-gray-700 dark:text-gray-300">
          Explore the{' '}
          <Link to="/matches" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            match schedule
          </Link>{' '}
          for fixtures at this ground, or jump to{' '}
          <Link to="/stadiums" className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
            all stadiums
          </Link>
          .
        </p>

        {cityGuide && (
          <p className="mt-2">
            <Link to={`/cities/${cityGuide.slug || cityGuide.id}`} className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
              {stadium.city} fan guide →
            </Link>
          </p>
        )}

        {itineraryHubForVenue && (
          <p className="mt-2">
            <Link to={itineraryHubForVenue} className="text-amber-600 dark:text-amber-400 hover:underline font-medium">
              Sample 3/5/7-day AI itineraries for this venue area →
            </Link>
          </p>
        )}

        {stadium.latitude && stadium.longitude && (
          <div className="mt-6 h-64 rounded-lg overflow-hidden">
            <Map markers={[{ lat: parseFloat(stadium.latitude), lng: parseFloat(stadium.longitude), label: stadium.name }]} />
          </div>
        )}

        <AdBanner className="my-6" slot="stadium-page" />

        <section className="mt-6 p-5 rounded-xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700" aria-labelledby="stadium-faq">
          <h2 id="stadium-faq" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Stadium FAQ
          </h2>
          <dl className="space-y-4">
            {stadiumFaq.map(({ question, answer }) => (
              <div key={question}>
                <dt className="font-medium text-gray-900 dark:text-white">{question}</dt>
                <dd className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">{answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <h3 className="font-bold mb-2">Transport</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              On match days, prioritize transit hubs and walking routes published by the host city. Rideshare pickup zones often move—follow local signage and official apps.
            </p>
          </div>
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
            <h3 className="font-bold mb-2">Stay &amp; dine</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              Compare hotels and tours from our hubs. Book flexible rates when possible; kickoff times can shift during the tournament.
            </p>
            <Link to="/hotels" className="text-sm text-amber-600 dark:text-amber-400 hover:underline mt-2 inline-block">
              Hotels →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default StadiumDetail;
